import type {
  Client,
  ClientDetail,
  ClientInvoice,
  ClientTeamMember,
  ClientTicket,
  ClientTimelineEvent,
} from "@/types";

interface RawEngagement {
  id: string;
  type: "call" | "email" | "meeting" | "note";
  title: string;
  timestamp: string;
  actorName: string;
}

interface RawInvoice {
  id: string;
  number: string;
  amount: number;
  status: string;
  dueDate: string;
  createDate: string;
}

interface CompanyDetailResponse {
  client: Client;
  engagements: RawEngagement[];
  invoices: RawInvoice[];
  clientTickets: ClientTicket[];
  vaTickets: ClientTicket[];
}

interface GmailMessage {
  id: string;
  subject: string;
  from: string;
  date: string;
  snippet: string;
}

interface ChatMessage {
  id: string;
  space: string;
  sender: string;
  text: string;
  createTime: string;
}

async function fetchJson<T>(url: string): Promise<T | null> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) return null;
  return (await res.json()) as T;
}

function mapInvoiceStatus(raw: string): ClientInvoice["status"] {
  const s = raw.toLowerCase();
  if (s === "paid") return "paid";
  if (s === "open") return "pending";
  if (s === "voided") return "draft";
  return "draft";
}

function mapEngagementsToTimeline(clientId: string, engagements: RawEngagement[]): ClientTimelineEvent[] {
  return engagements.map((e) => ({
    id: `hs-${e.id}`,
    clientId,
    source: "hubspot",
    type: e.type,
    title: e.title,
    detail: `Logged in HubSpot by ${e.actorName}`,
    actor: e.actorName,
    timestamp: e.timestamp,
  }));
}

function mapGmailToTimeline(clientId: string, messages: GmailMessage[]): ClientTimelineEvent[] {
  return messages.map((m) => ({
    id: `gmail-${m.id}`,
    clientId,
    source: "email",
    type: "email",
    title: m.subject,
    detail: m.snippet,
    actor: m.from,
    timestamp: m.date,
  }));
}

function mapChatToTimeline(clientId: string, messages: ChatMessage[]): ClientTimelineEvent[] {
  return messages.map((m) => ({
    id: `chat-${m.id}`,
    clientId,
    source: "google-chat",
    type: "chat message",
    title: m.space,
    detail: m.text,
    actor: m.sender,
    timestamp: m.createTime,
  }));
}

function buildRealTeam(client: Client): ClientTeamMember[] {
  const entries: ClientTeamMember[] = [];
  if (client.assignedAM && client.assignedAM !== "Unassigned") {
    entries.push({ role: "Account Manager", name: client.assignedAM, avatarColor: "#2563eb" });
  }
  if (client.assignedOM && client.assignedOM !== "Unassigned") {
    entries.push({ role: "Operations Manager", name: client.assignedOM, avatarColor: "#7c3aed" });
  }
  if (client.assignedTL && client.assignedTL !== "Unassigned") {
    entries.push({ role: "Team Leader", name: client.assignedTL, avatarColor: "#059669" });
  }
  return entries;
}

export async function fetchHubspotClients(): Promise<Client[]> {
  const res = await fetch("/api/integrations/hubspot/companies", { credentials: "include" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Failed to load clients (${res.status})`);
  }
  return (await res.json()) as Client[];
}

export async function fetchHubspotClientDetail(id: string): Promise<ClientDetail> {
  const res = await fetch(`/api/integrations/hubspot/company/${id}`, { credentials: "include" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Client not found: ${id}`);
  }
  const data = (await res.json()) as CompanyDetailResponse;
  const { client } = data;

  const hubspotEvents = mapEngagementsToTimeline(client.id, data.engagements);

  let emailEvents: ClientTimelineEvent[] = [];
  if (client.domain) {
    const gmail = await fetchJson<{ messages: GmailMessage[] }>(
      `/api/integrations/google/gmail?domain=${encodeURIComponent(client.domain)}`,
    );
    if (gmail) emailEvents = mapGmailToTimeline(client.id, gmail.messages);
  }

  // Google Chat spaces matching this client's name — same search-by-name scoping as Drive.
  const chat = await fetchJson<{ messages: ChatMessage[] }>(
    `/api/integrations/google/chat?query=${encodeURIComponent(client.name)}`,
  );
  const chatEvents = chat ? mapChatToTimeline(client.id, chat.messages) : [];

  const timeline = [...hubspotEvents, ...emailEvents, ...chatEvents].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  const invoices: ClientInvoice[] = data.invoices.map((i) => ({
    id: i.id,
    clientId: client.id,
    number: i.number,
    amount: i.amount,
    status: mapInvoiceStatus(i.status),
    issuedDate: i.createDate,
    dueDate: i.dueDate,
    paidDate: i.status.toLowerCase() === "paid" ? i.dueDate : undefined,
  }));

  return {
    client,
    timeline,
    invoices,
    revenueHistory: [],
    risks: [],
    recommendations: [],
    actionItems: [],
    renewals: [],
    team: buildRealTeam(client),
    meetings: [],
    performanceMetrics: [],
    clientTickets: data.clientTickets ?? [],
    vaTickets: data.vaTickets ?? [],
  };
}

export async function fetchClientFiles(query: string): Promise<Array<{ id: string; name: string; webViewLink?: string; modifiedTime?: string; mimeType?: string }>> {
  const res = await fetch(`/api/integrations/google/drive?query=${encodeURIComponent(query)}`, { credentials: "include" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Drive search failed (${res.status})`);
  }
  const data = (await res.json()) as { files: Array<{ id: string; name: string; webViewLink?: string; modifiedTime?: string; mimeType?: string }> };
  return data.files;
}
